import { useMemo } from 'react';

import { usePreviousValue } from '@tanium/react-use-previous-value';

import { GeneratorOutput } from '../../../hooks/useGeneratorOutput';
import { DataKeyType } from '../../../utils/DataProps';
import { NumberCoercible } from '../../../utils/NumberCoercible';
import { NumberScale } from '../../../utils/NumberScale';
import { PartialSeriesPoint } from '../../../utils/Series';
import { TimeScale } from '../../../utils/TimeScale';

type PointDatum = PartialSeriesPoint<[x: NumberCoercible, datum: unknown]>;

interface ScalePointProps {
  point: PointDatum;
  xScale: TimeScale;
  yScale: NumberScale;
}

export interface PointTransition {
  segmentIndex: number;
  xValue: NumberCoercible;
  baseCy: number;
  from: ScaledPoint;
  to: ScaledPoint;
}

export type PointTransitionOutput<TDataKeyType> = Map<TDataKeyType, Map<NumberCoercible, PointTransition>>;

export interface ScaledPoint {
  value?: number;
  cx: number;
  cy: number;
}
const scalePoint = ({ point, xScale, yScale }: ScalePointProps): ScaledPoint => {
  return {
    cx: xScale(point.data[0]) ?? 0,
    cy: yScale(point[1] ?? 0) ?? 0,
  };
};

const findFirstDefinedValue = (
  segment: PartialSeriesPoint<[x: NumberCoercible, datum: unknown]>[],
  backward = false,
) => {
  const start = backward ? segment.length - 1 : 0;
  const checkEnd = (index: number) => {
    return backward ? index > -1 : index < segment.length;
  };
  const increment = backward ? -1 : 1;

  for (let i = start; checkEnd(i); i += increment) {
    const [, value] = segment[i];
    if (value !== undefined) {
      return value;
    }
  }

  return 0;
};

const generateTransitionPoints = (baseSegment: PointDatum[], referencePoint?: PointDatum) => {
  let refValue = 0;
  if (baseSegment.length > 0 && referencePoint) {
    if (referencePoint[1] === undefined) {
      const refTime = referencePoint.data[0].valueOf();
      if (refTime < baseSegment[0].data[0].valueOf()) {
        // segment is at end
        refValue = findFirstDefinedValue(baseSegment);
      } else if (refTime > baseSegment[baseSegment.length - 1].data[0].valueOf()) {
        // segment is at start
        refValue = findFirstDefinedValue(baseSegment, true);
      }
    } else {
      [, refValue] = referencePoint;
    }
  }
  const transitionPoints = baseSegment.map((point) => {
    const [, value] = point;
    if (value === undefined) {
      return point;
    }
    return Object.assign([], { data: referencePoint ? [...referencePoint.data] : [point.data[0]] }, [0, refValue]);
  }) as PartialSeriesPoint<[x: NumberCoercible, datum: unknown]>[];

  return transitionPoints;
};

interface CreatePointTransitionGeneratorFnProps<TDataKeyType> {
  pointTransitionsMap: PointTransitionOutput<TDataKeyType>;
  xScale: TimeScale;
  yScale: NumberScale;
  prevXScale: TimeScale;
  prevYScale: NumberScale;
}
interface PointTransitionMapGeneratorProps<TDataKeyType> {
  dataSeriesKey: TDataKeyType;
  dataSegment: PointDatum[];
  mode: 'add' | 'remove' | 'update';
  segmentIndex: number;
  refPoint?: PointDatum;
  updatedDataSegment?: PointDatum[];
}
type MapTransitionFn<TDataKeyType> = (props: PointTransitionMapGeneratorProps<TDataKeyType>) => void;

const createPointTransitionGeneratorFn = <TDataKeyType,>({
  pointTransitionsMap,
  xScale,
  yScale,
  prevXScale,
  prevYScale,
}: CreatePointTransitionGeneratorFnProps<TDataKeyType>): MapTransitionFn<TDataKeyType> => {
  return ({
    dataSeriesKey,
    dataSegment,
    mode,
    segmentIndex,
    refPoint,
    updatedDataSegment,
  }: PointTransitionMapGeneratorProps<TDataKeyType>): void => {
    const transitionPoints = generateTransitionPoints(dataSegment, refPoint);
    for (let i = 0; i < dataSegment.length; i += 1) {
      let from;
      let to;
      let fromValue;
      let toValue;
      let xValue;
      if (mode === 'add') {
        from = transitionPoints[i];
        to = dataSegment[i];
        fromValue = undefined;
        [, toValue] = dataSegment[i];
        [xValue] = to.data;
      } else if (mode === 'remove') {
        from = dataSegment[i];
        to = transitionPoints[i];
        [, fromValue] = dataSegment[i];
        toValue = undefined;
        [xValue] = from.data;
      } else {
        from = dataSegment[i];
        to = updatedDataSegment ? updatedDataSegment[i] : dataSegment[i];
        [, fromValue] = dataSegment[i];
        [, toValue] = updatedDataSegment ? updatedDataSegment[i] : dataSegment[i];
        [xValue] = from.data;
      }

      const pointTransition = {
        segmentIndex,
        xValue,
        baseCy: yScale(0) ?? 0,
        from: {
          ...scalePoint({
            point: from,
            xScale: prevXScale,
            yScale: prevYScale,
          }),
          value: fromValue,
        },
        to: {
          ...scalePoint({
            point: to,
            xScale,
            yScale,
          }),
          value: toValue,
        },
      };
      if (pointTransitionsMap.has(dataSeriesKey)) {
        pointTransitionsMap.get(dataSeriesKey)?.set(xValue, pointTransition);
      } else {
        pointTransitionsMap.set(
          dataSeriesKey,
          new Map<NumberCoercible, PointTransition>().set(xValue, pointTransition),
        );
      }
    }
  };
};

export interface UseCustomPointTransition<TDataKeyType, TPointType, TDatum> {
  /**
   * Whether the data has changed
   */
  dataChanged: boolean;
  /**
   * The associated point produced by the generator.
   */
  generatorOutput: GeneratorOutput<TDataKeyType, TPointType, TDatum>;
  /**
   * Whether the shape should be animated
   */
  isAnimated: boolean;
  /**
   * The scale of X Values
   */
  xScale: TimeScale;
  /**
   * The scale of Y Values
   */
  yScale: NumberScale;
}

/**
 * Create custom intermediate segmented svg path strings between 2 paths that have different number of vertices
 * The intermediate paths looks as their start/end counter parts but are segmented and include dummy points so that point
 * orders don't change during interpolation
 */
export const useCustomPointTransition = <TDataKeyType extends DataKeyType, TPointType, TDatum>({
  dataChanged,
  generatorOutput,
  isAnimated,
  xScale,
  yScale,
}: UseCustomPointTransition<TDataKeyType, TPointType, TDatum>): PointTransitionOutput<TDataKeyType> => {
  const outputByKey = useMemo(
    () =>
      generatorOutput.reduce((acc, series) => {
        return acc.set(series.key, series);
      }, new Map<TDataKeyType, GeneratorOutput<TDataKeyType, TPointType, TDatum>[number]>()),
    [generatorOutput],
  );

  const prevOutputByKey = usePreviousValue(outputByKey);

  const prevXScale = usePreviousValue(xScale);
  const prevYScale = usePreviousValue(yScale);

  const pointTransitionsMap = useMemo(() => {
    const pointTransitionsByKey = new Map<TDataKeyType, Map<NumberCoercible, PointTransition>>();
    if (!isAnimated || !dataChanged) {
      return pointTransitionsByKey;
    }

    const addTransitionsToMap = createPointTransitionGeneratorFn<TDataKeyType>({
      pointTransitionsMap: pointTransitionsByKey,
      xScale,
      yScale,
      prevXScale,
      prevYScale,
    });

    generatorOutput.forEach((dataSeries) => {
      const prevDataSeries = prevOutputByKey.get(dataSeries.key);

      if (
        prevDataSeries &&
        dataSeries.filter((datum) => datum[1] !== undefined).length > 0 &&
        prevDataSeries.filter((datum) => datum[1] !== undefined).length !==
          dataSeries.filter((datum) => datum[1] !== undefined).length
      ) {
        // skip the generated empty segment at the beginning and end
        // (when other series in the same chart have data before or after this series)
        let startIndex = 0;
        let prevStartIndex = 0;

        let endIndex = dataSeries.length - 1;
        let prevEndIndex = prevDataSeries.length - 1;

        while (prevDataSeries[prevStartIndex][1] === undefined && dataSeries[startIndex][1] === undefined) {
          startIndex += 1;
          prevStartIndex += 1;
        }
        while (prevDataSeries[prevEndIndex][1] === undefined && dataSeries[endIndex][1] === undefined) {
          endIndex -= 1;
          prevEndIndex -= 1;
        }

        const trimmedDataSeries = dataSeries.slice(startIndex, endIndex + 1);
        const prevTrimmedDataSeries = prevDataSeries.slice(prevStartIndex, prevEndIndex + 1);

        let currentIndex = 0;
        let prevIndex = 0;
        let segmentIndex = 0;

        // this looping logic makes some assumptions about our generated data
        // 1.  Point series generated from our generator is unbroken. It always goes from start of series to end of series
        //     with an entry at every x value.  If there is no data there, the y value will be undefined
        // This loop runs O(N)  ( m+n to be specific)
        // This loop will only run if # of points has changed between input
        while (currentIndex < trimmedDataSeries.length && prevIndex < prevTrimmedDataSeries.length) {
          if (prevTrimmedDataSeries[prevIndex].data[0] < trimmedDataSeries[currentIndex].data[0]) {
            // items removed at beginning

            while (
              prevIndex < prevTrimmedDataSeries.length &&
              prevTrimmedDataSeries[prevIndex].data[0] < trimmedDataSeries[currentIndex].data[0]
            ) {
              prevIndex += 1;
            }

            // find the previous end point that has value
            const removedSegment = prevTrimmedDataSeries.slice(0, prevIndex);

            // because we trimmed all the undefined values up front, we can be sure that the first datum of the current data series
            // has valid value
            const refPoint = trimmedDataSeries[currentIndex];
            addTransitionsToMap({
              dataSeriesKey: dataSeries.key,
              dataSegment: removedSegment,
              mode: 'remove',
              segmentIndex,
              refPoint,
            });
            segmentIndex += 1;
          } else if (prevTrimmedDataSeries[prevIndex].data[0] > trimmedDataSeries[currentIndex].data[0]) {
            // items added at beginning

            while (
              currentIndex < trimmedDataSeries.length &&
              prevTrimmedDataSeries[prevIndex].data[0] > trimmedDataSeries[currentIndex].data[0]
            ) {
              currentIndex += 1;
            }

            const addedSegment = trimmedDataSeries.slice(0, currentIndex);
            // because we trimmed all the undefined values up front, we can be sure that the first datum of the previous data series
            // has valid value

            addTransitionsToMap({
              dataSeriesKey: dataSeries.key,
              dataSegment: addedSegment,
              mode: 'add',
              segmentIndex,
              refPoint: prevDataSeries[prevIndex],
            });

            segmentIndex += 1;
          } else if (
            trimmedDataSeries[currentIndex][1] === undefined &&
            prevTrimmedDataSeries[prevIndex][1] !== undefined
          ) {
            // items removed in the middle

            const removedSegmentStart = prevIndex;
            while (
              currentIndex < trimmedDataSeries.length &&
              prevIndex < prevTrimmedDataSeries.length &&
              trimmedDataSeries[currentIndex][1] === undefined &&
              prevTrimmedDataSeries[prevIndex][1] !== undefined
            ) {
              currentIndex += 1;
              prevIndex += 1;
            }
            const removedSegment = prevTrimmedDataSeries.slice(removedSegmentStart, prevIndex);
            addTransitionsToMap({
              dataSeriesKey: dataSeries.key,
              dataSegment: removedSegment,
              mode: 'remove',
              segmentIndex,
            });

            segmentIndex += 1;
          } else if (
            trimmedDataSeries[currentIndex][1] !== undefined &&
            prevTrimmedDataSeries[prevIndex][1] === undefined
          ) {
            // items added in the middle

            const addedSegmentStart = currentIndex;
            while (
              currentIndex < trimmedDataSeries.length &&
              prevIndex < prevTrimmedDataSeries.length &&
              trimmedDataSeries[currentIndex][1] !== undefined &&
              prevTrimmedDataSeries[prevIndex][1] === undefined
            ) {
              currentIndex += 1;
              prevIndex += 1;
            }
            const addedSegment = trimmedDataSeries.slice(addedSegmentStart, currentIndex);
            addTransitionsToMap({
              dataSeriesKey: dataSeries.key,
              dataSegment: addedSegment,
              mode: 'add',
              segmentIndex,
            });
            segmentIndex += 1;
          } else {
            const prevDataSegmentStartIndex = prevIndex;
            const dataSegmentStartIndex = currentIndex;
            while (
              currentIndex < trimmedDataSeries.length &&
              prevIndex < prevTrimmedDataSeries.length &&
              ((trimmedDataSeries[currentIndex][1] !== undefined &&
                prevTrimmedDataSeries[prevIndex][1] !== undefined) ||
                (trimmedDataSeries[currentIndex][1] === undefined && prevTrimmedDataSeries[prevIndex][1] === undefined))
            ) {
              currentIndex += 1;
              prevIndex += 1;
            }

            const prevDataSeriesSegment = prevTrimmedDataSeries.slice(prevDataSegmentStartIndex, prevIndex);
            const dataSeriesSegment = trimmedDataSeries.slice(dataSegmentStartIndex, currentIndex);

            addTransitionsToMap({
              dataSeriesKey: dataSeries.key,
              dataSegment: prevDataSeriesSegment.slice(0, -1),
              updatedDataSegment: dataSeriesSegment.slice(0, -1),
              mode: 'update',
              segmentIndex,
            });
            const isMiddleSegment =
              dataSeriesSegment.length > 1 &&
              (prevIndex < prevTrimmedDataSeries.length || currentIndex < trimmedDataSeries.length);
            addTransitionsToMap({
              dataSeriesKey: dataSeries.key,
              dataSegment: prevDataSeriesSegment.slice(-1),
              updatedDataSegment: dataSeriesSegment.slice(-1),
              mode: 'update',
              segmentIndex: isMiddleSegment ? segmentIndex + 1 : segmentIndex,
            });
            segmentIndex += 1;
          }
        }

        if (currentIndex < trimmedDataSeries.length) {
          // items added at the end

          if (currentIndex < trimmedDataSeries.length) {
            const addedSegment = trimmedDataSeries.slice(currentIndex);
            // because we trimmed all the undefined values from the end, we can be sure that the last datum of the previous data series
            // has valid value
            addTransitionsToMap({
              dataSeriesKey: dataSeries.key,
              dataSegment: addedSegment,
              mode: 'add',
              segmentIndex,
              refPoint: prevTrimmedDataSeries[prevIndex - 1],
            });
          }
        } else if (prevIndex < prevTrimmedDataSeries.length) {
          // items removed from the end

          if (prevIndex < prevTrimmedDataSeries.length) {
            const removedSegment = prevTrimmedDataSeries.slice(prevIndex);
            // because we trimmed all the undefined values from the end, we can be sure that the last datum of the previous data series
            // has valid value
            const refPoint = trimmedDataSeries[currentIndex - 1];
            addTransitionsToMap({
              dataSeriesKey: dataSeries.key,
              dataSegment: removedSegment,
              mode: 'remove',
              segmentIndex,
              refPoint,
            });
          }
        }
      }
    });
    return pointTransitionsByKey;
  }, [generatorOutput, xScale, yScale, prevXScale, prevYScale, prevOutputByKey, isAnimated, dataChanged]);

  return pointTransitionsMap;
};
